<script setup lang="ts">
import type { IWallet } from "@/user-account/types/UserAccount.type"
import type { PropType } from "vue"
import { fetchWithdrawal } from "@/app/api"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import { t } from "@/app/composables/useI18n"
import { toast } from "@/app/utils/notification.js"
import { ref, watch } from "vue"

const props = defineProps({
  currencyId: {
    type: Number,
    required: true,
  },
  providerId: {
    type: Number,
    required: true,
  },
  wallet: {
    type: Object as PropType<IWallet>,
    default: null,
  },
})

const withdrawalWallet = ref<IWallet | null>({
  balance: 0,
  currency: {
    sign: "",
  },
} as IWallet)
const loading = ref(false)
const amount = ref(0)

async function withdraw() {
  try {
    loading.value = true
    await fetchWithdrawal({
      provider_id: props.providerId,
      currency_id: props.currencyId,
      amount: amount.value,
    })
    toast.info(t("finance.withdrawal_success"))
  }
  catch {
    toast.error(t("errors.withdrawal_error"))
  }
  finally {
    loading.value = false
  }
}

watch(() => props.wallet, (newWallet) => {
  if (newWallet) {
    withdrawalWallet.value = newWallet
  }
}, { immediate: true })
</script>

<template>
  <div>
    <div class="amount-container">
      <div class="amount-title">
        <div>{{ t("finance.amount") }}</div>
        <div class="digits">
          {{ withdrawalWallet.currency.sign }}{{ withdrawalWallet.balance }}
        </div>
      </div>
      <div class="amount">
        <input v-model="amount" type="number" class="amount-input">
        <button class="max-btn" @click="amount = withdrawalWallet.balance">
          {{ t("general.max") }}
        </button>
      </div>
    </div>

    <BaseButton class="wide" :disabled="loading" @click="withdraw()">
      <span v-if="loading">{{ t("finance.loading") }}</span>
      <span v-else>{{ t("finance.withdraw") }}</span>
    </BaseButton>
  </div>
</template>

<style lang="scss" scoped>
.amount-container {
  margin: 10px 0px;
}

.bank {
  height: 40px;
  margin-bottom: 16px;
}

.label {
  margin-bottom: 7px;
  font-size: 14px;
}
.amount {
  border-radius: 4px;
  position: relative;
  display: flex;
  width: 100%;
  .amount-icon {
    position: absolute;
    top: 50%;
    transform: translate(8px, -50%);
  }

  .amount-input {
    border: none;
    background: var(--be-small-card-bg);
    height: 40px;
    border-radius: 4px 0 0 4px;
    outline: none;
    color: white;
    text-indent: 10px;
    width: 100%;
  }
}

.amount-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  margin-bottom: 10px;

  .digits {
    color: rgba(255, 255, 255, 0.5);
  }
}

.max-btn {
  height: 40px;
  width: max-content;
  background: var(--be-primary-btn);
  border: none;
  color: var(--be-primary-btn-label);
  font-family: Poppins;
  font-size: 14px;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
}

.withdraw-desc {
  font-family: Poppins;
  font-size: 12px;
}
</style>
